import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Register } from '@assetaccountant/core/models/register';
import { FinancialYear } from '@assetaccountant/core/pipes/aa-fin-year.pipe';
import { createFyRange, endDateToFY } from '@assetaccountant/core/resolvers/fy.resolver';
import { AppNavigationService } from '@assetaccountant/core/services/app-navigation.service';
import { RegisterService } from '@assetaccountant/core/services/register.service';
import { toUniversalDateString } from '@assetaccountant/core/utils/date-utils';
import moment from 'moment';
import { filter } from 'rxjs/operators';

interface FinancialYearNavItem extends FinancialYear {
  name?: string;
  isSelectionAvailable?: boolean;
  icon?: string;
  gridIcon?: boolean[];
}

function isFinancialYear(value: any): value is FinancialYear {
  return !!value.startDate && !!value.endDate;
}

@Component({
  selector: 'app-fy-selector',
  templateUrl: './fy-selector.component.html',
  styleUrls: ['./fy-selector.component.scss'],
  standalone: false,
})
export class FySelectorComponent implements OnChanges, OnDestroy {
  _selectedRegister?: Register;
  @Input() set selectedRegister(register: Register | undefined) {
    this._selectedRegister = register;

    this.updateNav();
  }

  @Output() onYearChanged = new EventEmitter<FinancialYear>();

  selectedFinancialYear?: FinancialYear;

  yearNavGroups: FinancialYearNavItem[][]; // Not precalc
  yearNavs: FinancialYearNavItem[]; // Precalc

  private _subscriptions = [
    this.router.events
      .pipe(filter((ev): ev is NavigationEnd => ev instanceof NavigationEnd))
      .subscribe(() => this.updateNav()),
  ];

  constructor(
    private appNavigationService: AppNavigationService,
    private registerService: RegisterService,
    private router: Router,
  ) {}

  ngOnDestroy() {
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('selectedRegister' in changes) {
      this.updateNav();
    }
  }

  async updateNav() {
    const startDate = this.appNavigationService.getPeriodStartDate();
    const endDate = this.appNavigationService.getPeriodEndDate();
    if (startDate && endDate) {
      this.selectedFinancialYear = {
        startDate,
        endDate,
      };
    } else {
      this.selectedFinancialYear = undefined;
    }

    const register = await this.registerService.getRegister().toPromise();

    if (register) {
      const now = moment();
      const nextYearStart = now
        .month(register.reportingYearStartDateMonth - 1)
        .date(register.reportingYearStartDateDay)
        .add(1, 'years');
      const thisYearEnd = nextYearStart.add(-1, 'days');
      const currentYearEnd = {
        year: thisYearEnd.year(),
        month: thisYearEnd.month() + 1,
        day: thisYearEnd.date(),
      };

      if (moment().isAfter(currentYearEnd)) {
        currentYearEnd.year += 1;
      }

      const currentYear = endDateToFY(toUniversalDateString(currentYearEnd));

      if (!isFinancialYear(currentYear)) {
        throw new Error('No start or end date for current year');
      }

      this.yearNavs = [
        {
          name: 'Current Year',
          ...currentYear,
          isSelectionAvailable: true,
          gridIcon: this.getGridIconArray(1, 12),
        },
      ];

      if (endDate) {
        const prevYear = endDateToFY(toUniversalDateString(moment.utc(endDate).add(-1, 'year')));
        const nextYear = endDateToFY(toUniversalDateString(moment.utc(endDate).add(1, 'year')));
        if (!isFinancialYear(prevYear) || !isFinancialYear(nextYear)) {
          throw new Error('No start or end date for previous/next year');
        }

        this.yearNavs.push(
          {
            name: 'Previous Year',
            ...prevYear,
            icon: 'arrow-left',
            isSelectionAvailable: this.isYearBeyondAvailableRange(prevYear, currentYear),
          },
          {
            name: 'Next Year',
            ...nextYear,
            icon: 'arrow-right',
            isSelectionAvailable: this.isYearBeyondAvailableRange(nextYear, currentYear),
          },
        );
      }
    }
  }

  async onUserChangedFinancialYear(finYear: FinancialYear) {
    await this.appNavigationService.setFinancialYear(
      createFyRange(finYear.startDate, finYear.endDate),
    );

    this.onYearChanged.emit(finYear);
  }

  private getGridIconArray(start: number, end: number): boolean[] {
    const icon: boolean[] = [];
    for (let index = 0; index < 12; index++) {
      icon.push(index >= start - 1 && index <= end - 1);
    }
    return icon;
  }

  private isYearBeyondAvailableRange(year: FinancialYear, currentYear: FinancialYear): boolean {
    return moment(year.endDate).year() <= moment(currentYear.endDate).year() + 1;
  }
}
